import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { CanalDeContacto, CarlineSelect, CodigoPostal, ConsentQuestion, DetalleMotivo, Loading, MultipleCTASelection, NombreApellidos, TelefonoEmail } from 'src/components';
import { useIsComerciales } from 'src/feature-app/hooks';
import { LayoutWrapper } from 'src/feature-app/Screen';
import { ConfirmacionLeadsNoQuestions, MapaLeads } from 'src/forms';
import { MapaLeadsInPage } from 'src/forms/Leads/InPage';
import { FormInfo, Steps } from 'src/types';
import { partialDataLeadsCallCenter } from '.';
import { LeadGenericoController } from '../LeadGenericoController';
import { LeadsFormsProps } from '../LeadGenericoForm';
import { useInitializeLeadForm } from '../use-initialize-lead-form';

const getMapaFields = (inPage: boolean) => {
	if (inPage === true) {
		return <MapaLeadsInPage formName="masInformacion" />;
	} else {
		return <MapaLeads formName="masInformacion" />;
	}
};

enum CallCenterSteps {
	CallCenterCampaign,
	FormObjective,
	Modelo,
	Mapa,
	NombreApellido,
	CodigoPostal,
	Contacto,
	EmailTelefono,
	Comentarios,
	Lssi,
	Tmk,
	Confirmacion,
}

export function CallCenterForm({ updatedFormData, inPage, isConfigurador, isLeadDealer }: LeadsFormsProps) {
	const isComerciales = useIsComerciales();
	const [partialData, setPartialData] = useState(null);
	const ctaFormObjective = [
		{
			label: 'Test Drive',
			value: 'Test Drive',
		},
		{
			label: 'Más Información',
			value: 'Más Información',
		},
		{
			label: 'Presupuesto',
			value: 'Presupuesto',
		},
		{
			label: 'Cita Previa',
			value: 'Cita Previa',
		},
	];
	const ctaCallCenterCampaign = [
		{
			label: 'Servicio habitual',
			value: 'Servicio habitual',
		},
		{
			label: 'ID.4 Renting Online',
			value: 'ID.4 Renting Online',
		},
		{
			label: 'No enviado',
			value: 'No enviado',
		},
	];

	useEffect(() => {
		if (updatedFormData) {
			setPartialData(updatedFormData);
		} else {
			setPartialData(partialDataLeadsCallCenter(isConfigurador));
		}
	}, []);

	const defaultSteps: Steps[] = [
		{
			title: (
				<>
					<Text bold>Origen</Text> del lead?
				</>
			),
			fields: (
				<LayoutWrapper>
					<MultipleCTASelection ctaValues={ctaCallCenterCampaign} name="callCenterCampaign" nextOnClick />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.CallCenterCampaign,
			name: 'CallCenterCampaign',
			outputs: ['callCenterCampaign'],
			outputsText: ['callCenterCampaign'],
			ctas: ctaCallCenterCampaign,
		},
		{
			title: (
				<>
					Que <Text bold>tipo</Text> de leads es?
				</>
			),
			fields: (
				<LayoutWrapper>
					<MultipleCTASelection ctaValues={ctaFormObjective} name="formObjective" nextOnClick />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.FormObjective,
			name: 'FormObjective',
			outputs: ['formObjective'],
			outputsText: ['formObjective'],
			ctas: ctaFormObjective,
		},
		{
			title: (
				<>
					<Text bold>¿Qué modelo </Text> interesa al cliente?
				</>
			),
			fields: (
				<LayoutWrapper>
					<CarlineSelect useCarline={true} />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.Modelo,
			name: 'Modelo',
			outputs: ['modelo'],
			outputsText: ['modelo'],
		},
		isLeadDealer
			? null
			: {
					screenIndex: CallCenterSteps.Mapa,
					name: 'Mapa',
					multiStep: true,
					component: getMapaFields(inPage),
			  },
		{
			title: (
				<>
					Para poder preparar su visita, necesitamos saber <Text bold>quién es</Text>
				</>
			),
			fields: (
				<LayoutWrapper>
					<NombreApellidos />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.NombreApellido,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<>
					También nos gustaría conocer <Text bold>su código postal</Text>
				</>
			),
			fields: (
				<LayoutWrapper>
					<CodigoPostal />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.CodigoPostal,
			name: 'CodigoPostal',
			outputs: ['codigoPostal'],
			outputsText: ['codigoPostal'],
		},
		{
			title: (
				<>
					<Text bold>¿Cómo prefiere </Text>que contactemos con él?
					<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
				</>
			),
			fields: (
				<LayoutWrapper>
					<CanalDeContacto />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.Contacto,
			name: 'Contacto',
			outputs: ['contacto'],
			outputsText: ['contacto'],
		},
		{
			title: (
				<>
					Para ello necesitaremos <Text bold>sus datos de contacto</Text>
				</>
			),
			fields: (
				<LayoutWrapper>
					<TelefonoEmail hidePrefijo={isComerciales} requiredTelefono requiredEmail />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.EmailTelefono,
			name: 'EmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			title: (
				<>
					¿Quiere saber <Text bold>algo en concreto</Text>?
				</>
			),
			fields: (
				<LayoutWrapper>
					<DetalleMotivo name="commentsCC" />
				</LayoutWrapper>
			),
			screenIndex: CallCenterSteps.Comentarios,
			name: 'Comentarios',
			outputs: ['commentsCC'],
			outputsText: ['commentsCC'],
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			screenIndex: CallCenterSteps.Lssi,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			screenIndex: CallCenterSteps.Tmk,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <ConfirmacionLeadsNoQuestions />,
			screenIndex: CallCenterSteps.Confirmacion,
			isLastStep: true,
			hidePreviousStep: true,
			name: 'Confirmacion',
		},
	];
	const filteredSteps = defaultSteps.filter((step) => step !== null);

	const { loading, steps, firstStep, lastStep } = useInitializeLeadForm(filteredSteps, false, inPage);

	const citaVentaInfo: Partial<FormInfo> = {
		numberOfScreens: steps.length,
	};

	return loading ? <Loading /> : <LeadGenericoController steps={steps} leadData={partialData} leadInfo={citaVentaInfo} firstStep={firstStep} lastStep={lastStep} inPage={inPage} isConfigurador={isConfigurador} />;
}
